import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '../utils';

type TabsProps<T extends string = string> = Omit<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
  'value' | 'defaultValue' | 'onValueChange'
> & {
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
};

const TabsInner = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    className={cn('bg-inherit', className)}
    {...props}
  />
));
TabsInner.displayName = TabsPrimitive.Root.displayName;

const Tabs = TabsInner as <T extends string = string>(
  props: TabsProps<T> & {
    ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Root>>;
  },
) => React.ReactElement;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'flex items-center justify-center w-fit text-muted-foreground text-sm font-medium gap-2 h-12',
      className,
    )}
    {...props}
  >
    {children}
  </TabsPrimitive.List>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    'aria-label' | 'id'
  > & {
    'aria-label': string;
    id: string;
  }
>(({ id, 'aria-label': ariaLabel, className, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    id={id}
    aria-label={ariaLabel}
    className={cn(
      'flex items-center w-full justify-center whitespace-nowrap p-3 transition-all',
      'disabled:pointer-events-none h-full relative border-b-2 border-transparent',
      'data-[state=active]:border-primary data-[state=active]:text-foreground',
      className,
    )}
    {...props}
  >
    {children}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-ring focus-visible:ring-offset-2',
      'data-[state=inactive]:hidden',
      'flex-1 min-h-0 flex flex-col',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
